import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useRoles } from '../hooks/useRoles.js';

const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon first
const DEFAULT_ROLES = ['kitchen', 'front_of_house', 'cashier', 'manager', 'bar', 'delivery']; // fallback only
const WEATHER_CONDITIONS = ['rain', 'sunny', 'hot', 'cold', 'storm', 'cloudy'];
const WEATHER_EMOJI = { rain: '🌧️', sunny: '☀️', hot: '🌡️', cold: '🥶', storm: '⛈️', cloudy: '☁️' };
const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin',
  'Australia/Hobart', 'Australia/Lord_Howe',
];

function roleLabel(r) {
  return r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function getEasterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
}

function addDaysDate(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}

function fmtShort(date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function getNthWeekday(year, month, weekday, n) {
  let count = 0;
  for (let d = 1; d <= 31; d++) {
    const date = new Date(year, month, d);
    if (date.getMonth() !== month) break;
    if (date.getDay() === weekday && ++count === n) return date;
  }
  return null;
}

function buildSpecialEvents(year) {
  const easter = getEasterSunday(year);
  const mothersDay = getNthWeekday(year, 4, 0, 2);
  const fathersDay = getNthWeekday(year, 8, 0, 1);
  const melbCup = getNthWeekday(year, 10, 2, 1);
  return [
    { type: 'public_holiday',  name: "Public Holiday",       date: "state-specific" },
    { type: 'christmas_season',name: "Christmas Season",     date: "22 Dec – 3 Jan" },
    { type: 'easter_week',     name: "Easter Week",          date: `${fmtShort(addDaysDate(easter, -3))} – ${fmtShort(addDaysDate(easter, 1))} ${year}` },
    { type: 'valentines_day',  name: "Valentine's Day",      date: `14/02/${year}` },
    { type: 'mothers_day',     name: "Mother's Day",         date: mothersDay ? `${fmtShort(mothersDay)}/${year}` : "2nd Sun of May" },
    { type: 'fathers_day',     name: "Father's Day",         date: fathersDay ? `${fmtShort(fathersDay)}/${year}` : "1st Sun of Sep" },
    { type: 'melbourne_cup',   name: "Melbourne Cup",        date: melbCup ? `${fmtShort(melbCup)}/${year}` : "1st Tue of Nov" },
    { type: 'halloween',       name: "Halloween",            date: `31/10/${year}` },
    { type: 'lunar_new_year',  name: "Lunar New Year",       date: "varies yearly" },
    { type: 'mid_autumn',      name: "Mid-Autumn Festival",  date: "varies yearly" },
    { type: 'diwali',          name: "Diwali",               date: "varies yearly" },
    { type: 'eid_al_fitr',     name: "Eid al-Fitr",          date: "varies yearly" },
    { type: 'eid_al_adha',     name: "Eid al-Adha",          date: "varies yearly" },
    { type: 'ramadan',         name: "Ramadan",              date: "varies yearly" },
  ];
}

const PREDICTHQ_EVENTS = [
  { type: 'concerts',       name: "Concerts" },
  { type: 'sports',         name: "Sports" },
  { type: 'festivals',      name: "Festivals" },
  { type: 'community',      name: "Community Events" },
  { type: 'conferences',    name: "Conferences" },
  { type: 'expos',          name: "Expos" },
  { type: 'performing-arts',name: "Performing Arts" },
];

const CURRENT_YEAR = new Date().getFullYear();
const SPECIAL_EVENTS = buildSpecialEvents(CURRENT_YEAR);
const ALL_EVENT_OPTIONS = [...SPECIAL_EVENTS, ...PREDICTHQ_EVENTS];

function eventLabel(type) {
  const found = ALL_EVENT_OPTIONS.find(e => e.type === type);
  return found ? found.name : type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function ChangePasswordCard({ api, endpoint }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg('');
    if (form.new_password.length < 8) { setMsg('New password must be at least 8 characters.'); return; }
    if (form.new_password !== form.confirm_password) { setMsg('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.put(endpoint, { current_password: form.current_password, new_password: form.new_password });
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      setMsg('Password updated.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.message || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <span className="card-title">Change Password</span>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} style={{ maxWidth: 400 }}>
          {msg && <div className={`alert alert-${msg === 'Password updated.' ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>{msg}</div>}
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" className="form-control" value={form.current_password} autoComplete="current-password"
              onChange={e => setForm(p => ({ ...p, current_password: e.target.value }))} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>New Password</label>
              <input type="password" className="form-control" value={form.new_password} autoComplete="new-password"
                placeholder="Min 8 characters"
                onChange={e => setForm(p => ({ ...p, new_password: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" className="form-control" value={form.confirm_password} autoComplete="new-password"
                onChange={e => setForm(p => ({ ...p, confirm_password: e.target.value }))} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner" /> : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const api = useApi();
  const { updateUser } = useAuth();
  const { roles: bizRoles, skills: bizSkills, roleNames, skillNames, setRoles: setBizRoles, setSkills: setBizSkills } = useRoles();
  const ROLES = roleNames.length > 0 ? roleNames : DEFAULT_ROLES;

  const [newRoleName, setNewRoleName] = useState('');
  const [newSkillName, setNewSkillName] = useState('');
  const [rolesMsg, setRolesMsg] = useState('');

  // Business settings
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    name: '', email: '', location: '', latitude: '', longitude: '', timezone: 'Australia/Sydney', state: 'NSW', brand_color: '#2563eb',
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoData, setLogoData] = useState(undefined); // undefined = not changed


  // Custom events
  const [customEventForm, setCustomEventForm] = useState({ date: '', name: '', adjustment: 1 });
  const [customEventSaving, setCustomEventSaving] = useState(false);
  const [customEventError, setCustomEventError] = useState('');
  const [showCustomEventForm, setShowCustomEventForm] = useState(false);

  // Rules
  const [rules, setRules] = useState({ staffing_requirements: [], weather_adjustments: [], event_adjustments: [], custom_events: [] });
  const [rulesLoading, setRulesLoading] = useState(true);

  // Staffing requirement modal
  const [showStaffingModal, setShowStaffingModal] = useState(false);
  const [staffingForm, setStaffingForm] = useState({
    days_of_week: [1], role: 'kitchen', min_staff: 1, max_staff: 3,
    shift_start: '09:00', shift_end: '17:00',
  });
  const [staffingSaving, setStaffingSaving] = useState(false);
  const [staffingError, setStaffingError] = useState('');

  // Weather adjustment modal
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [weatherForm, setWeatherForm] = useState({ condition: 'rain', role: 'kitchen', adjustment: 0 });
  const [weatherSaving, setWeatherSaving] = useState(false);
  const [weatherError, setWeatherError] = useState('');

  // Event adjustment modal
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ event_type: 'concerts', role: 'kitchen', adjustment: 1, time_of_day: 'all' });
  const [eventSaving, setEventSaving] = useState(false);
  const [eventError, setEventError] = useState('');

  const [pageError, setPageError] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setRulesLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get('/settings'),
        api.get('/rules'),
      ]);
      setSettings(s);
      setSettingsForm({
        name: s.name || '',
        email: s.email || '',
        location: s.location || '',
        latitude: s.latitude || '',
        longitude: s.longitude || '',
        timezone: s.timezone || 'Australia/Sydney',
        state: s.state || 'NSW',
        brand_color: s.brand_color || '#2563eb',
      });
      setLogoPreview(s.logo_url || null);
      setRules(r);
    } catch (err) {
      setPageError(err.message || 'Failed to load settings');
    } finally {
      setRulesLoading(false);
    }
  }

  async function handleSaveSettings(e) {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsMsg('');
    try {
      const body = {
        name: settingsForm.name,
        email: settingsForm.email,
        location: settingsForm.location || null,
        latitude: settingsForm.latitude ? parseFloat(settingsForm.latitude) : null,
        longitude: settingsForm.longitude ? parseFloat(settingsForm.longitude) : null,
        timezone: settingsForm.timezone,
        state: settingsForm.state,
        brand_color: settingsForm.brand_color,
      };
      if (logoData !== undefined) body.logo_url = logoData;
      const updated = await api.put('/settings', body);
      setSettings(updated);
      setLogoData(undefined);
      setLogoPreview(updated.logo_url || null);
      updateUser({ name: updated.name, email: updated.email, logo_url: updated.logo_url || null, brand_color: updated.brand_color || '#2563eb' });
      setSettingsMsg('Settings saved.');
      setTimeout(() => setSettingsMsg(''), 3000);
    } catch (err) {
      setSettingsMsg('Error: ' + err.message);
    } finally {
      setSettingsSaving(false);
    }
  }


  async function handleSaveCustomEvent(e) {
    e.preventDefault();
    setCustomEventError('');
    if (!customEventForm.date || !customEventForm.name) {
      setCustomEventError('Date and name are required.');
      return;
    }
    setCustomEventSaving(true);
    try {
      const row = await api.post('/rules/custom-events', {
        date: customEventForm.date,
        name: customEventForm.name.trim(),
        adjustment: parseInt(customEventForm.adjustment, 10),
      });
      setRules(prev => ({ ...prev, custom_events: [...(prev.custom_events || []), row].sort((a, b) => a.date.localeCompare(b.date)) }));
      setCustomEventForm({ date: '', name: '', adjustment: 1 });
      setShowCustomEventForm(false);
    } catch (err) {
      setCustomEventError(err.message);
    } finally {
      setCustomEventSaving(false);
    }
  }

  async function handleDeleteCustomEvent(id) {
    if (!window.confirm('Remove this event?')) return;
    try {
      await api.delete(`/rules/custom-events/${id}`);
      setRules(prev => ({ ...prev, custom_events: prev.custom_events.filter(e => e.id !== id) }));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleSaveStaffing(e) {
    e.preventDefault();
    setStaffingError('');
    if (staffingForm.days_of_week.length === 0) {
      setStaffingError('Select at least one day.');
      return;
    }
    setStaffingSaving(true);
    try {
      const rows = await Promise.all(
        staffingForm.days_of_week.map(day =>
          api.post('/rules/staffing', {
            day_of_week: day,
            role: staffingForm.role,
            min_staff: parseInt(staffingForm.min_staff, 10),
            max_staff: parseInt(staffingForm.max_staff, 10),
            shift_start: staffingForm.shift_start,
            shift_end: staffingForm.shift_end,
          })
        )
      );
      setRules(prev => {
        let updated = [...prev.staffing_requirements];
        for (const row of rows) {
          updated = updated.filter(r => !(r.day_of_week === row.day_of_week && r.role === row.role));
          updated.push(row);
        }
        updated.sort((a, b) => a.day_of_week - b.day_of_week || a.role.localeCompare(b.role));
        return { ...prev, staffing_requirements: updated };
      });
      setShowStaffingModal(false);
    } catch (err) {
      setStaffingError(err.message);
    } finally {
      setStaffingSaving(false);
    }
  }

  async function handleDeleteStaffing(id) {
    if (!window.confirm('Remove this staffing requirement?')) return;
    try {
      await api.delete(`/rules/staffing/${id}`);
      setRules(prev => ({ ...prev, staffing_requirements: prev.staffing_requirements.filter(r => r.id !== id) }));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleSaveWeather(e) {
    e.preventDefault();
    setWeatherError('');
    setWeatherSaving(true);
    try {
      const row = await api.post('/rules/weather', {
        condition: weatherForm.condition,
        role: weatherForm.role,
        adjustment: parseInt(weatherForm.adjustment, 10),
      });
      setRules(prev => {
        const filtered = prev.weather_adjustments.filter(
          r => !(r.condition === row.condition && r.role === row.role)
        );
        return { ...prev, weather_adjustments: [...filtered, row] };
      });
      setShowWeatherModal(false);
    } catch (err) {
      setWeatherError(err.message);
    } finally {
      setWeatherSaving(false);
    }
  }

  async function handleDeleteWeather(id) {
    if (!window.confirm('Remove this weather adjustment?')) return;
    try {
      await api.delete(`/rules/weather/${id}`);
      setRules(prev => ({ ...prev, weather_adjustments: prev.weather_adjustments.filter(r => r.id !== id) }));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleSaveEvent(e) {
    e.preventDefault();
    setEventError('');
    setEventSaving(true);
    try {
      const row = await api.post('/rules/event', {
        event_type: eventForm.event_type,
        role: eventForm.role,
        adjustment: parseInt(eventForm.adjustment, 10),
        time_of_day: eventForm.time_of_day,
      });
      setRules(prev => {
        const filtered = prev.event_adjustments.filter(
          r => !(r.event_type === row.event_type && r.role === row.role && r.time_of_day === row.time_of_day)
        );
        return { ...prev, event_adjustments: [...filtered, row] };
      });
      setShowEventModal(false);
    } catch (err) {
      setEventError(err.message);
    } finally {
      setEventSaving(false);
    }
  }

  async function handleDeleteEvent(id) {
    if (!window.confirm('Remove this event adjustment?')) return;
    try {
      await api.delete(`/rules/event/${id}`);
      setRules(prev => ({ ...prev, event_adjustments: prev.event_adjustments.filter(r => r.id !== id) }));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleAddRole(type) {
    const name = type === 'role' ? newRoleName.trim() : newSkillName.trim();
    if (!name) return;
    try {
      const row = await api.post('/rules/roles', { name, type });
      if (type === 'role') { setBizRoles(prev => [...prev, row]); setNewRoleName(''); }
      else { setBizSkills(prev => [...prev, row]); setNewSkillName(''); }
      setRolesMsg('Saved.');
      setTimeout(() => setRolesMsg(''), 2000);
    } catch (err) {
      setRolesMsg('Error: ' + err.message);
    }
  }

  async function handleDeleteRole(id, type) {
    if (!window.confirm(`Remove this ${type}?`)) return;
    try {
      await api.delete(`/rules/roles/${id}`);
      if (type === 'role') setBizRoles(prev => prev.filter(r => r.id !== id));
      else setBizSkills(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Business info, staffing rules, and smart adjustments</p>
        </div>
      </div>

      {pageError && <div className="alert alert-error">{pageError}</div>}

      {/* ── Business Settings ── */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Business Information</span>
          {settings && (
            <span className="text-muted text-sm">Restaurant ID: <strong>{settings.slug}</strong></span>
          )}
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveSettings}>
            <div className="form-group">
              <label>Restaurant Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="logo"
                    style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', padding: 4 }}
                  />
                ) : (
                  <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
                    No logo
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 500000) { alert('Image must be under 500KB.'); return; }
                      const reader = new FileReader();
                      reader.onload = ev => {
                        setLogoPreview(ev.target.result);
                        setLogoData(ev.target.result);
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <label htmlFor="logo-upload" className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    Upload Logo
                  </label>
                  {logoPreview && (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 8, color: 'var(--color-danger)' }}
                      onClick={() => { setLogoPreview(null); setLogoData(null); }}>
                      Remove
                    </button>
                  )}
                  <div className="form-hint" style={{ marginTop: 4 }}>PNG or JPG, max 500KB</div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Brand Colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="color"
                  value={settingsForm.brand_color}
                  onChange={e => setSettingsForm(p => ({ ...p, brand_color: e.target.value }))}
                  style={{ width: 48, height: 36, padding: 2, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', background: 'none' }}
                />
                <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-muted)' }}>{settingsForm.brand_color}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSettingsForm(p => ({ ...p, brand_color: '#2563eb' }))}
                  style={{ color: 'var(--text-muted)' }}
                >
                  Reset
                </button>
              </div>
              <div className="form-hint">Applied to buttons, tabs, and highlights on the staff site</div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Restaurant Name</label>
                <input className="form-control" value={settingsForm.name}
                  onChange={e => setSettingsForm(p => ({ ...p, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" className="form-control" value={settingsForm.email}
                  onChange={e => setSettingsForm(p => ({ ...p, email: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label>Location Address</label>
              <input className="form-control" value={settingsForm.location}
                placeholder="e.g. 123 Main St, Sydney NSW 2000"
                onChange={e => setSettingsForm(p => ({ ...p, location: e.target.value }))} />
              <div className="form-hint">Used for weather and nearby events lookup</div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input type="number" step="any" className="form-control" value={settingsForm.latitude}
                  placeholder="-33.8688"
                  onChange={e => setSettingsForm(p => ({ ...p, latitude: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input type="number" step="any" className="form-control" value={settingsForm.longitude}
                  placeholder="151.2093"
                  onChange={e => setSettingsForm(p => ({ ...p, longitude: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select className="form-control" value={settingsForm.timezone}
                  onChange={e => setSettingsForm(p => ({ ...p, timezone: e.target.value }))}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>State / Territory</label>
                <select className="form-control" value={settingsForm.state}
                  onChange={e => setSettingsForm(p => ({ ...p, state: e.target.value }))}>
                  <option value="NSW">NSW – New South Wales</option>
                  <option value="VIC">VIC – Victoria</option>
                  <option value="QLD">QLD – Queensland</option>
                  <option value="SA">SA – South Australia</option>
                  <option value="WA">WA – Western Australia</option>
                  <option value="TAS">TAS – Tasmania</option>
                  <option value="NT">NT – Northern Territory</option>
                  <option value="ACT">ACT – Australian Capital Territory</option>
                </select>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Used for correct public holiday detection and penalty rate warnings.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" className="btn btn-primary" disabled={settingsSaving}>
                {settingsSaving ? <span className="spinner" /> : 'Save Settings'}
              </button>
              {settingsMsg && (
                <span style={{ color: settingsMsg.startsWith('Error') ? 'var(--color-danger)' : 'var(--color-success)', fontSize: 13 }}>
                  {settingsMsg}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>


      {/* ── Custom Events ── */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Custom Events</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCustomEventForm(true)}>
            + Add Event
          </button>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
            Add local events (council festivals, markets, school holidays) to adjust staffing on specific dates.
          </p>
          {showCustomEventForm && (
            <form onSubmit={handleSaveCustomEvent} style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              {customEventError && <div className="alert alert-error" style={{ marginBottom: 8 }}>{customEventError}</div>}
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" className="form-control" value={customEventForm.date}
                    onChange={e => setCustomEventForm(p => ({ ...p, date: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Event Name</label>
                  <input className="form-control" value={customEventForm.name}
                    placeholder="e.g. Warringah Council Street Festival"
                    onChange={e => setCustomEventForm(p => ({ ...p, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Staff Adjustment</label>
                  <input type="number" className="form-control" value={customEventForm.adjustment}
                    min="-20" max="20"
                    onChange={e => setCustomEventForm(p => ({ ...p, adjustment: e.target.value }))} />
                  <div className="form-hint">+2 = busier, -1 = quieter</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={customEventSaving}>
                  {customEventSaving ? <span className="spinner" /> : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => { setShowCustomEventForm(false); setCustomEventError(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          {rulesLoading ? (
            <div className="text-muted text-sm">Loading...</div>
          ) : (rules.custom_events || []).length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No custom events added yet.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Adjustment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(rules.custom_events || []).map(ce => (
                  <tr key={ce.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(ce.date).toLocaleDateString('en-AU', { dateStyle: 'medium' })}</td>
                    <td>{ce.name}</td>
                    <td>
                      <span style={{ color: ce.adjustment > 0 ? 'var(--color-success)' : ce.adjustment < 0 ? 'var(--color-danger)' : 'var(--text-muted)', fontWeight: 600 }}>
                        {ce.adjustment > 0 ? `+${ce.adjustment}` : ce.adjustment}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }}
                        onClick={() => handleDeleteCustomEvent(ce.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Staffing Requirements ── */}
      <div className="card mb-4">
        <div className="card-header ch-stack-380">
          <span className="card-title">Staffing Requirements</span>
          <button className="btn btn-primary btn-sm" onClick={() => {
            setStaffingForm({ days_of_week: [1], role: 'kitchen', min_staff: 1, max_staff: 3, shift_start: '09:00', shift_end: '17:00' });
            setStaffingError('');
            setShowStaffingModal(true);
          }}>
            + Add Requirement
          </button>
        </div>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          {rulesLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <span className="spinner spinner-dark" style={{ width: 24, height: 24 }} />
            </div>
          ) : rules.staffing_requirements.length === 0 ? (
            <div className="empty-state" style={{ padding: 32 }}>
              <p>No staffing requirements set. Add requirements to enable roster generation.</p>
              <button className="btn btn-primary btn-sm mt-2" onClick={() => { setStaffingForm({ days_of_week: [1], role: 'kitchen', min_staff: 1, max_staff: 3, shift_start: '09:00', shift_end: '17:00' }); setShowStaffingModal(true); }}>
                Add Your First Requirement
              </button>
            </div>
          ) : (
            <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
              <table>
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Shift Hours</th>
                    <th>Min – Max</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...rules.staffing_requirements]
                    .sort((a, b) => DAY_ORDER.indexOf(a.day_of_week) - DAY_ORDER.indexOf(b.day_of_week))
                    .map((req, i, sorted) => {
                      const isFirstOfDay = i === 0 || req.day_of_week !== sorted[i - 1].day_of_week;
                      return (
                    <React.Fragment key={req.id}>
                      {isFirstOfDay && (
                        <tr className="table-group-label">
                          <td colSpan={4}>{DAYS_FULL[req.day_of_week]}</td>
                        </tr>
                      )}
                      <tr>
                      <td data-label="Role"><span className="badge badge-primary">{roleLabel(req.role)}</span></td>
                      <td data-label="Shift Hours">{req.shift_start?.slice(0,5)} – {req.shift_end?.slice(0,5)}</td>
                      <td data-label="Min–Max">{req.min_staff} – {req.max_staff}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-ghost btn-sm" onClick={() => {
                            setStaffingForm({
                              days_of_week: [req.day_of_week],
                              role: req.role,
                              min_staff: req.min_staff,
                              max_staff: req.max_staff,
                              shift_start: req.shift_start?.slice(0,5),
                              shift_end: req.shift_end?.slice(0,5),
                            });
                            setStaffingError('');
                            setShowStaffingModal(true);
                          }}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }}
                            onClick={() => handleDeleteStaffing(req.id)}>Remove</button>
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Weather Adjustments ── */}
      <div className="card mb-4">
        <div className="card-header ch-stack-426">
          <span className="card-title">Weather-Based Adjustments</span>
          <button className="btn btn-primary btn-sm" onClick={() => {
            setWeatherForm({ condition: 'rain', role: 'kitchen', adjustment: 0 });
            setWeatherError('');
            setShowWeatherModal(true);
          }}>
            + Add Adjustment
          </button>
        </div>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <p className="text-muted text-sm mb-3">
            Define how weather affects staffing. A positive number adds staff, negative reduces staff.
            Each restaurant is different — a shopping centre may get busier when it rains.
          </p>
          {rules.weather_adjustments.length === 0 ? (
            <p className="text-muted text-sm">No weather adjustments configured.</p>
          ) : (
            <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
              <table>
                <thead>
                  <tr><th>Condition</th><th>Role</th><th>Adjustment</th><th></th></tr>
                </thead>
                <tbody>
                  {[...rules.weather_adjustments]
                    .sort((a, b) => WEATHER_CONDITIONS.indexOf(a.condition) - WEATHER_CONDITIONS.indexOf(b.condition))
                    .map((wa, i, sorted) => {
                      const isFirstOfCondition = i === 0 || wa.condition !== sorted[i - 1].condition;
                      return (
                    <React.Fragment key={wa.id}>
                      {isFirstOfCondition && (
                        <tr className="table-group-label">
                          <td colSpan={4}>{WEATHER_EMOJI[wa.condition]} {wa.condition.charAt(0).toUpperCase() + wa.condition.slice(1)}</td>
                        </tr>
                      )}
                      <tr>
                      <td data-label="Condition"><span className="badge badge-info">{WEATHER_EMOJI[wa.condition] || ''} {wa.condition}</span></td>
                      <td data-label="Role">{roleLabel(wa.role)}</td>
                      <td data-label="Adj.">
                        <span style={{ color: wa.adjustment > 0 ? 'var(--color-success)' : wa.adjustment < 0 ? 'var(--color-danger)' : 'inherit', fontWeight: 600 }}>
                          {wa.adjustment > 0 ? '+' : ''}{wa.adjustment}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }}
                          onClick={() => handleDeleteWeather(wa.id)}>Remove</button>
                      </td>
                    </tr>
                    </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Event Adjustments ── */}
      <div className="card mb-4">
        <div className="card-header ch-stack-397">
          <span className="card-title">Event-Based Adjustments</span>
          <button className="btn btn-primary btn-sm" onClick={() => {
            setEventForm({ event_type: 'mothers_day', role: 'kitchen', adjustment: 1, time_of_day: 'all' });
            setEventError('');
            setShowEventModal(true);
          }}>
            + Add Adjustment
          </button>
        </div>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <p className="text-muted text-sm mb-3">
            Adjust staffing for special days and nearby events. Special days are detected automatically — select from the list to set your staffing adjustment.
          </p>
          {rules.event_adjustments.length === 0 ? (
            <p className="text-muted text-sm">No event adjustments configured.</p>
          ) : (
            <div className="table-wrap table-wrap-nostack" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
              <table>
                <thead>
                  <tr><th>Event Type</th><th>Role</th><th>Time of Day</th><th>Adjustment</th><th></th></tr>
                </thead>
                <tbody>
                  {rules.event_adjustments.map(ea => (
                    <tr key={ea.id}>
                      <td data-label="Event"><span className="badge badge-secondary">{eventLabel(ea.event_type)}</span></td>
                      <td data-label="Role">{roleLabel(ea.role)}</td>
                      <td data-label="Time of Day" style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{ea.time_of_day || 'All Day'}</td>
                      <td data-label="Adj.">
                        <span style={{ color: ea.adjustment > 0 ? 'var(--color-success)' : ea.adjustment < 0 ? 'var(--color-danger)' : 'inherit', fontWeight: 600 }}>
                          {ea.adjustment > 0 ? '+' : ''}{ea.adjustment}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }}
                          onClick={() => handleDeleteEvent(ea.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Custom Roles & Skills ── */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Roles & Skills</span>
          {rolesMsg && <span style={{ fontSize: 12, color: rolesMsg.startsWith('Error') ? 'var(--color-danger)' : 'var(--color-success)' }}>{rolesMsg}</span>}
        </div>
        <div className="card-body">
          <div className="roles-skills-grid">
            {/* Roles */}
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Roles</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {bizRoles.map(r => (
                  <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--bg-subtle, #f3f4f6)', borderRadius: 12, padding: '3px 10px', fontSize: 12 }}>
                    {roleLabel(r.name)}
                    <button type="button" onClick={() => handleDeleteRole(r.id, 'role')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-control" style={{ flex: 1 }} placeholder="New role name..." value={newRoleName}
                  onChange={e => setNewRoleName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddRole('role'))} />
                <button className="btn btn-primary btn-sm" onClick={() => handleAddRole('role')}>Add</button>
              </div>
              <div className="form-hint">Used for staffing rules, shifts, and roster generation.</div>
            </div>
            {/* Skills */}
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Skills</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {bizSkills.map(s => (
                  <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--bg-subtle, #f3f4f6)', borderRadius: 12, padding: '3px 10px', fontSize: 12 }}>
                    {roleLabel(s.name)}
                    <button type="button" onClick={() => handleDeleteRole(s.id, 'skill')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1, padding: 0 }}>✕</button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="form-control" style={{ flex: 1 }} placeholder="New skill name..." value={newSkillName}
                  onChange={e => setNewSkillName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddRole('skill'))} />
                <button className="btn btn-primary btn-sm" onClick={() => handleAddRole('skill')}>Add</button>
              </div>
              <div className="form-hint">Assigned to employees for skill-matching in roster generation.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Change Password ── */}
      <ChangePasswordCard api={api} endpoint="/auth/owner/change-password" />

      {/* ── Australian Labour Law Info ── */}
      <div className="card mb-4">
        <div className="card-header">
          <span className="card-title">Australian Labour Law (Restaurant Industry Award 2020)</span>
          <span className="badge badge-success">Hardcoded</span>
        </div>
        <div className="card-body">
          <p className="text-muted text-sm mb-3">
            These rules are automatically enforced during roster generation. They cannot be disabled.
          </p>
          <div className="labour-law-grid">
            {[
              { label: 'Max weekly hours (full-time)', value: '38 hours' },
              { label: 'Max shift length', value: '12 hours' },
              { label: 'Min break between shifts', value: '10 hours' },
              { label: 'Max consecutive working days', value: '6 days' },
              { label: 'Min shift length (casual)', value: '3 hours' },
              { label: 'Meal break after', value: '5 hours of work' },
              { label: 'Saturday penalty rate', value: '125%' },
              { label: 'Sunday penalty rate', value: '150%' },
              { label: 'Public holiday penalty rate', value: '225%' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span className="text-muted">{item.label}</span>
                <span style={{ fontWeight: 600 }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Staffing Modal ── */}
      {showStaffingModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowStaffingModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Staffing Requirement</span>
              <button className="modal-close" onClick={() => setShowStaffingModal(false)}>&#x2715;</button>
            </div>
            <form onSubmit={handleSaveStaffing}>
              <div className="modal-body">
                {staffingError && <div className="alert alert-error">{staffingError}</div>}
                <div className="form-group">
                  <label>Days <span className="required">*</span></label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {DAY_ORDER.map(i => {
                      const d = DAYS_FULL[i];
                      const selected = staffingForm.days_of_week.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setStaffingForm(p => ({
                            ...p,
                            days_of_week: selected
                              ? p.days_of_week.filter(x => x !== i)
                              : [...p.days_of_week, i].sort((a, b) => a - b),
                          }))}
                          style={{
                            padding: '8px 12px', borderRadius: 16, fontSize: 13, cursor: 'pointer', border: '1px solid',
                            borderColor: selected ? 'var(--color-primary)' : 'var(--border)',
                            background: selected ? 'var(--color-primary)' : 'transparent',
                            color: selected ? '#fff' : 'var(--text-muted)',
                            fontWeight: selected ? 600 : 400,
                          }}
                        >
                          {d.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="form-group">
                  <label>Role <span className="required">*</span></label>
                  <select className="form-control" value={staffingForm.role}
                    onChange={e => setStaffingForm(p => ({ ...p, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Min Staff</label>
                    <input type="number" min="0" max="20" className="form-control" value={staffingForm.min_staff}
                      onChange={e => setStaffingForm(p => ({ ...p, min_staff: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Max Staff</label>
                    <input type="number" min="1" max="20" className="form-control" value={staffingForm.max_staff}
                      onChange={e => setStaffingForm(p => ({ ...p, max_staff: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Shift Start <span className="required">*</span></label>
                    <input type="time" className="form-control" value={staffingForm.shift_start}
                      onChange={e => setStaffingForm(p => ({ ...p, shift_start: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label>Shift End <span className="required">*</span></label>
                    <input type="time" className="form-control" value={staffingForm.shift_end}
                      onChange={e => setStaffingForm(p => ({ ...p, shift_end: e.target.value }))} required />
                  </div>
                </div>
                <div className="form-hint">
                  Select multiple days to apply the same rule to each. If a (Day, Role) combination already exists it will be updated.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowStaffingModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={staffingSaving}>
                  {staffingSaving ? <span className="spinner" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Weather Modal ── */}
      {showWeatherModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowWeatherModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Weather Adjustment</span>
              <button className="modal-close" onClick={() => setShowWeatherModal(false)}>&#x2715;</button>
            </div>
            <form onSubmit={handleSaveWeather}>
              <div className="modal-body">
                {weatherError && <div className="alert alert-error">{weatherError}</div>}
                <div className="form-group">
                  <label>Weather Condition <span className="required">*</span></label>
                  <select className="form-control" value={weatherForm.condition}
                    onChange={e => setWeatherForm(p => ({ ...p, condition: e.target.value }))}>
                    {WEATHER_CONDITIONS.map(c => <option key={c} value={c}>{WEATHER_EMOJI[c]} {c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Role <span className="required">*</span></label>
                  <select className="form-control" value={weatherForm.role}
                    onChange={e => setWeatherForm(p => ({ ...p, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Staff Adjustment <span className="required">*</span></label>
                  <input type="number" min="-10" max="10" className="form-control" value={weatherForm.adjustment}
                    onChange={e => setWeatherForm(p => ({ ...p, adjustment: e.target.value }))} />
                  <div className="form-hint">
                    Positive = add staff, Negative = reduce staff. E.g. +2 means add 2 extra staff when this weather occurs.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWeatherModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={weatherSaving}>
                  {weatherSaving ? <span className="spinner" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Event Modal ── */}
      {showEventModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEventModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Event Adjustment</span>
              <button className="modal-close" onClick={() => setShowEventModal(false)}>&#x2715;</button>
            </div>
            <form onSubmit={handleSaveEvent}>
              <div className="modal-body">
                {eventError && <div className="alert alert-error">{eventError}</div>}
                <div className="form-group">
                  <label>Event Type <span className="required">*</span></label>
                  <select className="form-control" value={eventForm.event_type}
                    onChange={e => setEventForm(p => ({ ...p, event_type: e.target.value }))} required>
                    <optgroup label="Special Days">
                      {SPECIAL_EVENTS.map(ev => (
                        <option key={ev.type} value={ev.type}>{ev.name} — {ev.date}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Nearby Events (PredictHQ)">
                      {PREDICTHQ_EVENTS.map(ev => (
                        <option key={ev.type} value={ev.type}>{ev.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div className="form-group">
                  <label>Role <span className="required">*</span></label>
                  <select className="form-control" value={eventForm.role}
                    onChange={e => setEventForm(p => ({ ...p, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Time of Day</label>
                  <select className="form-control" value={eventForm.time_of_day}
                    onChange={e => setEventForm(p => ({ ...p, time_of_day: e.target.value }))}>
                    <option value="all">All Day</option>
                    <option value="morning">Morning (before 12pm)</option>
                    <option value="lunch">Lunch (12pm – 5pm)</option>
                    <option value="dinner">Dinner (5pm+)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Staff Adjustment <span className="required">*</span></label>
                  <input type="number" min="-10" max="10" className="form-control" value={eventForm.adjustment}
                    onChange={e => setEventForm(p => ({ ...p, adjustment: e.target.value }))} />
                  <div className="form-hint">Positive = add staff, Negative = reduce staff.</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEventModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={eventSaving}>
                  {eventSaving ? <span className="spinner" /> : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
